import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, FormRow, PageHeader, ProgressBar, StatusBadge } from '../../components/ui';
import { byId, fullName, getProgramCourses, getProgramProgress } from '../../utils/lmsSelectors';

function matches(text, query) {
  return String(text || '').toLowerCase().includes(query.trim().toLowerCase());
}

function CourseBundleSelector({ data, selectedIds, setSelectedIds }) {
  const [query, setQuery] = useState('');
  const courses = data.courses.filter((course) => course.status !== 'archived');
  const filtered = query ? courses.filter((course) => matches(`${course.title} ${course.category} ${course.status}`, query)) : courses;

  const toggleCourse = (courseId) => {
    setSelectedIds(selectedIds.includes(courseId)
      ? selectedIds.filter((id) => id !== courseId)
      : [...selectedIds, courseId]);
  };

  return (
    <div className="selector-panel">
      <div className="toolbar between">
        <strong>Select courses for this program</strong>
        <input className="search-input" placeholder="Search course by title, category, or status" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <div className="checkbox-grid scroll-box">
        {filtered.map((course) => (
          <label key={course.id} className="check-row">
            <input type="checkbox" checked={selectedIds.includes(course.id)} onChange={() => toggleCourse(course.id)} />
            <span>{course.title}<small className="muted"> • {course.category} • {course.status}</small></span>
          </label>
        ))}
      </div>
      <div className="selected-stack">
        <strong>Selected order</strong>
        {selectedIds.length === 0 && <p className="muted small">No courses selected yet.</p>}
        {selectedIds.map((courseId, index) => {
          const course = byId(data.courses, courseId);
          return (
            <div key={courseId} className="selected-item-row">
              <span>{index + 1}. {course?.title}</span>
              <div className="button-row compact">
                <button type="button" className="button button-ghost" disabled={index === 0} onClick={() => {
                  const next = [...selectedIds];
                  [next[index - 1], next[index]] = [next[index], next[index - 1]];
                  setSelectedIds(next);
                }}>Up</button>
                <button type="button" className="button button-ghost" disabled={index === selectedIds.length - 1} onClick={() => {
                  const next = [...selectedIds];
                  [next[index + 1], next[index]] = [next[index], next[index + 1]];
                  setSelectedIds(next);
                }}>Down</button>
                <button type="button" className="button button-danger" onClick={() => setSelectedIds(selectedIds.filter((id) => id !== courseId))}>Remove</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminPrograms() {
  const { data, currentUser, createProgram, updateProgram, archiveProgram } = useLms();
  const [editingId, setEditingId] = useState('');
  const editingProgram = editingId ? byId(data.programs || [], editingId) : null;
  const [form, setForm] = useState({ title: '', description: '', category: data.categories[0] || 'Company Process', status: 'draft' });
  const [courseIds, setCourseIds] = useState([]);
  const [message, setMessage] = useState('');
  const [query, setQuery] = useState('');

  const beginEdit = (program) => {
    setEditingId(program.id);
    setForm({ title: program.title, description: program.description, category: program.category, status: program.status });
    setCourseIds(getProgramCourses(data, program.id).map((item) => item.courseId));
    setMessage(`Editing ${program.title}.`);
  };

  const resetForm = () => {
    setEditingId('');
    setForm({ title: '', description: '', category: data.categories[0] || 'Company Process', status: 'draft' });
    setCourseIds([]);
  };

  const submit = (event) => {
    event.preventDefault();
    const payload = { ...form, courseIds };
    const result = editingProgram ? updateProgram(editingProgram.id, payload, currentUser.id) : createProgram(payload, currentUser.id);
    setMessage(result.ok ? editingProgram ? 'Program updated.' : 'Program created.' : result.message);
    if (result.ok && !editingProgram) resetForm();
  };

  const rows = useMemo(() => (data.programs || [])
    .filter((program) => !query || matches(`${program.title} ${program.category} ${program.status}`, query))
    .map((program) => {
      const courses = getProgramCourses(data, program.id);
      const assignments = (data.programAssignments || []).filter((assignment) => assignment.programId === program.id && assignment.status !== 'revoked');
      const completed = assignments.filter((assignment) => getProgramProgress(data, assignment).status === 'completed').length;
      return {
        ...program,
        courses: courses.length,
        assigned: assignments.length,
        completion: assignments.length ? Math.round((completed / assignments.length) * 100) : 0,
        createdByName: fullName(byId(data.users, program.createdBy))
      };
    }), [data, query]);

  return (
    <>
      <PageHeader title="Program / Bundle management" eyebrow="Admin" description="Bundle multiple courses into a program, keep the course order, publish the program, and assign it from the Assignments page." />
      <div className="grid two uneven">
        <Card>
          <h2>{editingProgram ? 'Edit program' : 'Create program'}</h2>
          <form className="form-stack" onSubmit={submit}>
            <FormRow label="Program title"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Example: Battery Design Training Program" /></FormRow>
            <FormRow label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
            <div className="form-grid">
              <FormRow label="Category"><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{data.categories.map((category) => <option key={category}>{category}</option>)}</select></FormRow>
              <FormRow label="Status"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option></select></FormRow>
            </div>
            <CourseBundleSelector data={data} selectedIds={courseIds} setSelectedIds={setCourseIds} />
            <div className="button-row">
              <button className="button button-primary" type="submit">{editingProgram ? 'Save program' : 'Create program'}</button>
              {editingProgram && <button className="button button-ghost" type="button" onClick={resetForm}>Cancel edit</button>}
            </div>
            {message && <p className="feedback-box compact-feedback">{message}</p>}
          </form>
        </Card>
        <Card>
          <h2>Program rules</h2>
          <ul className="check-list">
            <li>Programs are course bundles. Learners complete each course in the selected order.</li>
            <li>Only published programs can be assigned.</li>
            <li>Only published courses inside a program are assigned to learners.</li>
            <li>Program grading averages the course grades generated from quizzes and graded exercises.</li>
          </ul>
        </Card>
      </div>
      <Card>
        <div className="toolbar between">
          <h2>Program register</h2>
          <input className="search-input" placeholder="Search program by title, category, or status" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <DataTable
          rows={rows}
          columns={[
            { key: 'title', label: 'Program', render: (row) => <div><strong>{row.title}</strong><p className="muted small">Created by {row.createdByName}</p></div> },
            { key: 'category', label: 'Category' },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'courses', label: 'Courses' },
            { key: 'assigned', label: 'Assigned learners' },
            { key: 'completion', label: 'Completion', render: (row) => <ProgressBar value={row.completion} /> },
            { key: 'actions', label: 'Actions', render: (row) => (
              <div className="button-row compact">
                <button className="button button-secondary" type="button" onClick={() => beginEdit(row)}>Edit</button>
                <Link className="button button-ghost" to="/admin/assignments">Assign</Link>
                <button className="button button-danger" type="button" disabled={row.status === 'archived'} onClick={() => archiveProgram(row.id, currentUser.id)}>Archive</button>
              </div>
            ) }
          ]}
        />
      </Card>
    </>
  );
}
